import { NgClass } from '@angular/common';
import { Component, computed, input, model, signal } from '@angular/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import {
  AvatarBorderType,
  AvatarComponent
} from '@app/shared/components/group-avatars/avatar/proffeo-avatar.component';
import { AvatarSize } from '@app/shared/types/enums/avatar-size.enum';
import { User } from '@app/shared/types/models/user/user.model';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { ProffeoUiInputValue } from '@app/shared/ui/types/input.types';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Searchable multi-user checklist. The selection lives in the `selectedUsers` model (user ids);
 * locked users are shown disabled and keep their selection state, including on "select all".
 */
@Component({
  selector: 'proffeo-select-users-list',
  imports: [TranslatePipe, MatCheckboxModule, UiInputComponent, AvatarComponent, NgClass],
  templateUrl: './select-users-list.component.html'
})
export class SelectUsersListComponent {
  private readonly users = computed((): User[] => this.originalUsers() ?? []);
  private readonly lockedIds = computed((): ReadonlySet<string> => new Set(this.lockedUserIds() ?? []));
  private readonly selectedIds = computed((): ReadonlySet<string> => new Set(this.selectedUsers() ?? []));
  private readonly selectableUsers = computed((): User[] =>
    this.users().filter(user => !this.lockedIds().has(user.id))
  );

  protected readonly AvatarSize = AvatarSize;
  protected readonly AvatarBorderType = AvatarBorderType;
  protected readonly search = signal<ProffeoUiInputValue>('');
  protected readonly filteredUsers = computed((): User[] => {
    const searchValue = String(this.search() ?? '').toLowerCase();
    return searchValue ? this.users().filter(user => user.matchesSearch(searchValue)) : this.users();
  });
  protected readonly allSelected = computed((): boolean => {
    const selectable = this.selectableUsers();
    const selected = this.selectedIds();
    return selectable.length > 0 && selectable.every(user => selected.has(user.id));
  });

  public readonly originalUsers = input<User[]>([]);
  public readonly lockedUserIds = input<string[]>([]);
  public readonly selectedUsers = model<string[]>([]);

  protected isSelected(userId: string): boolean {
    return this.selectedIds().has(userId);
  }

  protected isLocked(userId: string): boolean {
    return this.lockedIds().has(userId);
  }

  protected onSelectAllChange(event: MatCheckboxChange): void {
    const lockedSelected = (this.selectedUsers() ?? []).filter(id => this.lockedIds().has(id));
    const selectable = event.checked ? this.selectableUsers().map(user => user.id) : [];
    this.selectedUsers.set([...new Set([...lockedSelected, ...selectable])]);
  }

  protected onUserCheckboxChange(event: MatCheckboxChange, user: User): void {
    const current = (this.selectedUsers() ?? []).filter(id => id !== user.id);
    this.selectedUsers.set(event.checked ? [...current, user.id] : current);
  }
}
